import React from "react";
import { FaTimes } from "react-icons/fa";
import { Input, Select, Textarea, Button } from "../ui";
import SpintaxHelp from "../flow/SpintaxHelp";

const EVENT_LABELS = {
  "user.signup": "User Signup",
  "user.trial_started": "Trial Started",
  "user.trial_expiring": "Trial Expiring",
  "user.subscription_expired": "Subscription Expired",
  "user.plan_upgraded": "Plan Upgraded",
  "user.plan_downgraded": "Plan Downgraded",
  "session.disconnected_persistent": "WhatsApp Disconnected",
  "user.inactive": "User Inactive",
  "user.low_message_quota": "Low Message Quota",
  "user.payment_failed": "Payment Failed",
};

const EVENT_CATEGORIES = {
  "User Lifecycle": ["user.signup", "user.trial_started", "user.trial_expiring", "user.inactive"],
  "Subscription": ["user.plan_upgraded", "user.plan_downgraded", "user.subscription_expired", "user.payment_failed"],
  "WhatsApp": ["session.disconnected_persistent", "user.low_message_quota"],
};

const VARIABLES = [
  { label: "Full Name", value: "{{name}}" },
  { label: "First Name", value: "{{firstName}}" },
  { label: "Last Name", value: "{{lastName}}" },
  { label: "User Email", value: "{{email}}" },
  { label: "Plan Name", value: "{{planName}}" },
  { label: "Session ID", value: "{{sessionId}}" },
  { label: "Expiry Date", value: "{{expiryDate}}" },
  { label: "Old Plan", value: "{{oldPlan}}" },
  { label: "Unsubscribe URL", value: "{{unsubscribe_url}}" },
  { label: "Unsubscribe Link", value: "{{unsubscribe_link}}" },
  { label: "Phone", value: "{{phone}}" },
];

export default function AutomationNodeConfigPanel({ node, onChange, onClose, onDelete, sessions }) {
  if (!node) return null;

  const data = node.data || {};
  const updateData = (key, value) => {
    onChange({ ...node, data: { ...data, [key]: value } });
  };

  const insertVar = (field, variable) => {
    updateData(field, (data[field] || "") + variable);
  };

  const VariableHelper = ({ field }) => (
    <div className="flex flex-wrap gap-1 mt-1">
      {VARIABLES.map((v) => (
        <button
          key={v.value}
          onClick={() => insertVar(field, v.value)}
          className="px-1.5 py-0.5 text-2xs bg-surface-bg rounded border border-surface-border hover:bg-brand-50 hover:text-brand-600 transition-colors"
          title={`Insert ${v.label}`}
        >
          {v.value}
        </button>
      ))}
    </div>
  );

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink-primary">
          {node.type === "eventTrigger" && "Event Trigger"}
          {node.type === "sendEmail" && "Send Email"}
          {node.type === "sendWhatsApp" && "Send WhatsApp"}
          {node.type === "delay" && "Delay"}
          {node.type === "condition" && "Condition"}
          {node.type === "httpRequest" && "HTTP Request"}
        </h3>
        <button onClick={onClose} className="text-ink-muted hover:text-ink-primary">
          <FaTimes size={14} />
        </button>
      </div>

      <div className="space-y-3">
        {/* ─── EVENT TRIGGER ──────────────────── */}
        {node.type === "eventTrigger" && (
          <div>
            <label className="block text-xs font-medium text-ink-secondary mb-1">Trigger Event</label>
            <Select value={data.event || ""} onChange={(e) => updateData("event", e.target.value)}>
              <option value="">Select event...</option>
              {Object.entries(EVENT_CATEGORIES).map(([cat, events]) => (
                <optgroup key={cat} label={cat}>
                  {events.map((ev) => (
                    <option key={ev} value={ev}>{EVENT_LABELS[ev]}</option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>
        )}

        {/* ─── SEND EMAIL ────────────────────── */}
        {node.type === "sendEmail" && (
          <>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">To (leave empty for user's email)</label>
              <Input value={data.to || ""} onChange={(e) => updateData("to", e.target.value)} placeholder="user@example.com or {{email}}" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Template Key (optional)</label>
              <Input value={data.templateKey || ""} onChange={(e) => updateData("templateKey", e.target.value)} placeholder="e.g. welcome, trial_expiring_3d" />
              <p className="text-2xs text-ink-muted mt-0.5">Uses existing email template if set. Leave empty for custom body.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Subject</label>
              <Input value={data.subject || ""} onChange={(e) => updateData("subject", e.target.value)} placeholder="Welcome to Walytic, {{name}}!" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">
                Email Body (HTML supported)
              </label>
              <Textarea
                value={data.htmlBody || data.body || ""}
                onChange={(e) => updateData("htmlBody", e.target.value)}
                placeholder={'<h2>Hello {{name}}</h2>\n<p>Welcome to Walytic!</p>\n<p style="font-size:12px">{{unsubscribe_link}}</p>'}
                rows={8}
                className="font-mono text-xs"
              />
              <VariableHelper field="htmlBody" />
              <p className="text-2xs text-ink-muted mt-1">
                Tip: Use <code className="bg-surface-bg px-1 rounded">{"{{unsubscribe_link}}"}</code> to add an unsubscribe link.
              </p>
            </div>
          </>
        )}

        {/* ─── SEND WHATSAPP ─────────────────── */}
        {node.type === "sendWhatsApp" && (
          <>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Phone Number</label>
              <Input value={data.to || ""} onChange={(e) => updateData("to", e.target.value)} placeholder="919876543210" />
              <p className="text-2xs text-ink-muted mt-0.5">Leave empty to use user's phone (if available).</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">WhatsApp Session</label>
              <Select value={data.sessionId || ""} onChange={(e) => updateData("sessionId", e.target.value)}>
                <option value="">Select session...</option>
                {(sessions || []).map((s) => (
                  <option key={s.sessionId} value={s.sessionId}>{s.number || s.sessionId}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Message</label>
              <Textarea
                value={data.message || ""}
                onChange={(e) => updateData("message", e.target.value)}
                placeholder={"Hi {{name}}, {welcome|good to have you|thanks for joining} on Walytic! 🎉"}
                rows={4}
              />
              <VariableHelper field="message" />
              <SpintaxHelp />
            </div>
          </>
        )}

        {/* ─── DELAY ─────────────────────────── */}
        {node.type === "delay" && (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-ink-secondary mb-1">Duration</label>
              <Input
                type="number"
                min="1"
                value={data.amount || ""}
                onChange={(e) => updateData("amount", e.target.value)}
                placeholder="5"
              />
            </div>
            <div className="w-28">
              <label className="block text-xs font-medium text-ink-secondary mb-1">Unit</label>
              <Select value={data.unit || "m"} onChange={(e) => updateData("unit", e.target.value)}>
                <option value="s">Seconds</option>
                <option value="m">Minutes</option>
                <option value="h">Hours</option>
                <option value="d">Days</option>
              </Select>
            </div>
          </div>
        )}

        {/* ─── CONDITION ─────────────────────── */}
        {node.type === "condition" && (
          <>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Variable</label>
              <Select value={data.variable || ""} onChange={(e) => updateData("variable", e.target.value)}>
                <option value="">Select variable...</option>
                <option value="planName">Plan Name</option>
                <option value="name">User Name</option>
                <option value="email">User Email</option>
                <option value="sessionId">Session ID</option>
                <option value="oldPlan">Old Plan</option>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Operator</label>
              <Select value={data.operator || "equals"} onChange={(e) => updateData("operator", e.target.value)}>
                <option value="equals">Equals</option>
                <option value="not_equals">Not Equals</option>
                <option value="contains">Contains</option>
                <option value="not_empty">Not Empty</option>
                <option value="empty">Is Empty</option>
              </Select>
            </div>
            {data.operator !== "not_empty" && data.operator !== "empty" && (
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">Value</label>
                <Input value={data.value || ""} onChange={(e) => updateData("value", e.target.value)} placeholder="e.g. pro, starter" />
              </div>
            )}
            <p className="text-2xs text-ink-muted">
              Connect the <span className="text-green-600 font-medium">Yes</span> and <span className="text-red-600 font-medium">No</span> handles to different branches.
            </p>
          </>
        )}

        {/* ─── HTTP REQUEST ──────────────────── */}
        {node.type === "httpRequest" && (
          <>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Method</label>
              <Select value={data.method || "POST"} onChange={(e) => updateData("method", e.target.value)}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">URL</label>
              <Input value={data.url || ""} onChange={(e) => updateData("url", e.target.value)} placeholder="https://api.mailwizz.example.com/..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Body (JSON)</label>
              <Textarea
                value={typeof data.body === "string" ? data.body : JSON.stringify(data.body || {}, null, 2)}
                onChange={(e) => {
                  try { updateData("body", JSON.parse(e.target.value)); } catch { updateData("body", e.target.value); }
                }}
                rows={4}
                className="font-mono text-xs"
                placeholder='{"email": "{{email}}", "name": "{{name}}"}'
              />
              <VariableHelper field="body" />
            </div>
          </>
        )}

        {/* ─── DELETE ────────────────────────── */}
        {node.type !== "eventTrigger" && (
          <div className="pt-3 border-t border-surface-border">
            <Button variant="danger" onClick={() => onDelete(node.id)} className="w-full text-xs">
              Delete Node
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
